import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { ChipSelect, EmptyState, HexAvatar, LevelMark, Loading } from '../components';
import { Link } from '../router';
import { useApp } from '../store';
import type { PlayerProfile } from '../types';
import { CommunityTabs } from './Guilds';

export function Players() {
  const { games } = useApp();
  const [players, setPlayers] = useState<PlayerProfile[]>([]);
  const [gameFilter, setGameFilter] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const handle = window.setTimeout(() => {
      api.players({ game: gameFilter || undefined, q: query || undefined })
        .then((data) => alive && setPlayers(data.players))
        .catch(() => alive && setPlayers([]))
        .finally(() => alive && setLoading(false));
    }, query ? 250 : 0);
    return () => { alive = false; window.clearTimeout(handle); };
  }, [gameFilter, query]);

  return (
    <div className="page">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Community</span>
          <h1>Players</h1>
        </div>
      </div>

      <CommunityTabs active="players" />

      <div className="player-filters">
        <label className="search-box">
          <Search size={15} />
          <input value={query} onChange={(event) => { setLoading(true); setQuery(event.target.value); }} placeholder="Search by name" aria-label="Search players" />
        </label>
        <ChipSelect
          options={[{ value: '', label: 'All games' }, ...games.map((game) => ({ value: game.slug, label: game.name }))]}
          selected={[gameFilter]}
          onToggle={(value) => { setLoading(true); setGameFilter(value); }}
          single
        />
      </div>

      {loading ? (
        <Loading label="Loading players…" />
      ) : players.length === 0 ? (
        <EmptyState title="No players found" hint="Try a different search, or invite your friends to TeamCraft." />
      ) : (
        <div className="player-grid">
          {players.map((player) => (
            <Link key={player.id} to={`/players/${player.username}`} className="player-card">
              <HexAvatar label={player.avatar} icon={player.avatarIcon} accent={player.avatarColor} />
              <div className="player-card-body">
                <strong>{player.displayName}</strong>
                {player.bio && <p>{player.bio}</p>}
                <div className="tag-row">
                  {player.reputation.tags.map((tag) => <span key={tag}>{tag}</span>)}
                </div>
              </div>
              <div className="player-card-side">
                <LevelMark level={player.progression.level} title={player.progression.title} />
                {player.reputation.playAgainRate !== null && (
                  <span className="mono muted">{player.reputation.playAgainRate}% play again</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
